import { useState, useEffect } from "react";
import { HashIcon, LoaderIcon } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";

interface SlackChannel {
  id: string;
  name: string;
}

interface SlackChannelSelectorProps {
  workspaceSlug: string;
  value?: string;
  onValueChange: (channelId: string, channelName: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function SlackChannelSelector({
  workspaceSlug,
  value,
  onValueChange,
  placeholder = "Select a channel",
  disabled = false,
}: SlackChannelSelectorProps) {
  const [channels, setChannels] = useState<SlackChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const { showToast } = useToast();

  useEffect(() => {
    const fetchChannels = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `/workspaces/${workspaceSlug}/slack/channels`,
        );

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || "Failed to fetch Slack channels");
        }

        const data = await response.json();
        setChannels(data.channels || []);
      } catch (error) {
        showToast(
          "error",
          error instanceof Error
            ? error.message
            : "Failed to fetch Slack channels",
        );
        setChannels([]);
      } finally {
        setLoading(false);
      }
    };

    fetchChannels();
  }, [workspaceSlug, showToast]);

  const handleValueChange = (channelId: string) => {
    const channel = channels.find((c) => c.id === channelId);
    if (channel) {
      onValueChange(channelId, channel.name);
    }
  };

  if (loading) {
    return (
      <div className="w-full relative rounded-xl bg-white/80 dark:bg-zinc-900/80 pl-10 py-3 text-base shadow-sm border-2 border-gray-300 dark:border-zinc-700 flex items-center">
        <HashIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-500" />
        <span className="text-zinc-500 dark:text-zinc-400 flex items-center gap-2">
          <LoaderIcon className="size-4 animate-spin" />
          Loading channels...
        </span>
      </div>
    );
  }

  if (channels.length === 0) {
    return (
      <div className="w-full relative rounded-xl bg-white/80 dark:bg-zinc-900/80 pl-10 py-3 text-base shadow-sm border-2 border-gray-300 dark:border-zinc-700">
        <HashIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-500" />
        <span className="text-zinc-500 dark:text-zinc-400">
          No channels available
        </span>
      </div>
    );
  }

  return (
    <Select value={value} onValueChange={handleValueChange} disabled={disabled}>
      <SelectTrigger leftIcon={<HashIcon className="size-4" />}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {channels.map((channel) => (
          <SelectItem key={channel.id} value={channel.id}>
            {channel.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
